import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AnimatedModal from '../AnimatedModal';
import { formatLocalDate } from '../../utils/timeUtils';
import './seasons.css';

/**
 * Season page → Matches → "Add matches": put existing matches (created without the season) into
 * this season. The server syncs the season's settings onto pending / live matches
 * (services/seasonMatchSync.js); finished ones are just linked (season stats + leaderboard).
 * @param {{ isOpen, onClose, seasonId, seasonName, onAdded: (result) => void }} props
 */
export default function AddMatchesModal({ isOpen, onClose, seasonId, seasonName, onAdded }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const seq = useRef(0);

  useEffect(() => { if (isOpen) { setQ(''); setPicked(new Set()); setError(''); } }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const my = ++seq.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/api/seasons/${seasonId}/addable-matches`, { params: q.trim() ? { q: q.trim() } : {} });
        if (my === seq.current) setRows(res.data || []);
      } catch (e) {
        if (my === seq.current) setError((e.response && e.response.data && e.response.data.error) || t('seasonMatches.loadFailed', 'Could not load matches'));
      } finally { if (my === seq.current) setLoading(false); }
    }, q ? 250 : 0);
    return () => clearTimeout(timer);
  }, [isOpen, q, seasonId, t]);

  const toggle = (id) => setPicked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const submit = async () => {
    if (saving || !picked.size) return;
    setSaving(true); setError('');
    try {
      const res = await axios.post(`/api/seasons/${seasonId}/matches`, { match_ids: [...picked] });
      const failed = (res.data.results || []).filter(r => r.error);
      if (failed.length) setError(failed.map(f => `#${f.match_id}: ${f.error}`).join(' · '));
      onAdded(res.data, failed.length === 0);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || t('seasonMatches.addFailed', 'Could not add the matches'));
    } finally { setSaving(false); }
  };

  const statusColor = { pending: 'var(--accent-amber)', live: 'var(--accent-green)', finished: 'var(--accent-blue)', cancelled: 'var(--accent-red)' };

  return (
    <AnimatedModal isOpen={isOpen} onClose={() => { if (!saving) onClose(); }} title={t('seasonMatches.addTitle', 'Add matches to {{name}}', { name: seasonName || t('seasonMatches.season', 'Season') })} maxWidth="640px">
      <div className="sp-addm">
        <p className="sp-addm-hint">{t('seasonMatches.addHint', 'For matches created without this season. Pending and live matches get the season\'s settings (match rules, CVARs, chat prefix, broadcast, halftime, temp admins, stage seating, TeamSpeak, Akros, HLTV). Finished matches are only linked, so they count in the season\'s stats and leaderboard. Teams, server, maps and veto stay as they are.')}</p>
        <input className="sp-input" autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('seasonMatches.addSearch', 'Search by team name or match #')} />
        <div className="sp-addm-list">
          {loading && !rows.length ? <div className="sp-addm-empty">{t('seasonMatches.loading', 'Loading matches...')}</div>
            : !rows.length ? <div className="sp-addm-empty">{t('seasonMatches.addNone', 'No other matches found')}</div>
            : rows.map(m => {
              const locked = m.bracket_locked;
              const on = picked.has(m.id);
              return (
                <label key={m.id} className={`sp-addm-row${on ? ' on' : ''}${locked ? ' locked' : ''}`}
                  title={locked ? t('seasonMatches.addBracketLocked', 'A bracket match stays in its bracket\'s season') : undefined}>
                  <input type="checkbox" checked={on} disabled={locked} onChange={() => toggle(m.id)} />
                  <span className="sp-addm-id">#{m.id}</span>
                  <span className="sp-addm-main">
                    <span className="sp-addm-teams">{m.team1_name || 'Team 1'} <span className="sp-muted">vs</span> {m.team2_name || 'Team 2'}</span>
                    <span className="sp-addm-sub">
                      {m.map || 'TBD'}{m.best_of > 1 ? ` · BO${m.best_of}` : ''}{m.server_name ? ` · ${m.server_name}` : ''} · {formatLocalDate(m.created_at)}
                      {m.season_name ? ` · ${t('seasonMatches.addInSeason', 'now in {{name}}', { name: m.season_name })}` : ''}
                    </span>
                  </span>
                  <span className="sp-addm-status" style={{ color: statusColor[m.status] || 'var(--text-muted)' }}>{t(`matchStatus.${m.status}`, m.status)}</span>
                </label>
              );
            })}
        </div>
        {error && <div className="sp-addm-error">{error}</div>}
        <div className="sp-addm-foot">
          <button type="button" className="sp-btn ghost" onClick={onClose} disabled={saving}>{t('common.cancel', 'Cancel')}</button>
          <button type="button" className="sp-btn" onClick={submit} disabled={saving || !picked.size}>
            {saving ? t('seasonMatches.adding', 'Adding…') : t('seasonMatches.addN', 'Add {{count}} match(es)', { count: picked.size })}
          </button>
        </div>
      </div>
    </AnimatedModal>
  );
}
